import { WarningAmberRounded } from '@mui/icons-material';
import { Box, Typography, Paper } from '@mui/material';
import { type ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { ThemePalette } from 'mui.theme';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useAppSelector } from 'store/hook';
import { isAllTimeLineDataEmpty } from 'utils/driverBehavior';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { truncateTo2dp } from 'utils/helpers/general';
import { AssetWiseTimeLineChartTooltip } from 'views/FleetPlanner/CreateDispatchPlan/Steps/TimelineStep/components/AssetWiseTimelineChartTooltip';
import { type PlanDataType } from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import {
  locationColors,
  shiftColorPalette,
} from 'views/FleetPlanner/utils/constants';
import {
  aggregateSchedulePlanByBumperNumber,
  normalizeSiteKey,
} from 'views/FleetPlanner/utils/dataFormatters';
import { type DispatchPlanGroupedByBumperNoType } from '../types/dispatch-summary.types';
import { getPlanTimeRange } from '../utils/dataGetters';
import { mergeDispatchPlans } from '../utils/dataFormatters';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const generateSeriesForAsset = (
  deviceData: PlanDataType[],
  bumperNumber: string
) => {
  const timeLineData: any[] = [];
  if (!Array.isArray(deviceData)) return [];

  deviceData.forEach((item: PlanDataType) => {
    try {
      const start = dayjs.utc(item.start_ts);
      const end = dayjs.utc(item.end_ts);

      if (!start.isValid() || !end.isValid() || start.valueOf() > end.valueOf())
        return;

      let colors = { fill: '#808080', stroke: '#A9A9A9' };
      let actionType = '';

      if (item?.action === 'loading') {
        const locationKey = normalizeSiteKey(
          item?.start_location
        ).toLowerCase();
        colors = locationColors[locationKey] || colors;
        actionType = 'Loading';
        timeLineData.push({
          x: bumperNumber,
          y: [start.valueOf(), end.valueOf()],
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          extraDetails: {
            ...item,
            action: actionType,
            start: start.valueOf(),
            end: end.valueOf(),
            startLocation: item?.start_location,
            endLocation: item?.end_location,
          },
        });
      } else if (item?.action === 'dumping') {
        colors = {
          fill: 'rgba(220, 38, 38, 0.3)',
          stroke: 'rgba(220, 38, 38, 0.7)',
        };
        actionType = 'Dumping';
        timeLineData.push({
          x: bumperNumber,
          y: [start.valueOf(), end.valueOf()],
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          extraDetails: {
            ...item,
            action: actionType,
            start: start.valueOf(),
            end: end.valueOf(),
            startLocation: item?.start_location,
            endLocation: item?.end_location,
          },
        });
      }
    } catch (innerErr) {
      console.error(`Error parsing item for ${bumperNumber}:`, innerErr);
    }
  });

  return [{ name: '', data: timeLineData }];
};

const SingleAssetTimelineRow = ({
  totalTrips,
  totalTons,
  assetSeries,
  assetChartOptions,
  label,
}: {
  totalTrips: number;
  totalTons: number;
  assetSeries: any;
  assetChartOptions: any;
  label: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: '160px',
          flexShrink: 0,
          padding: '2px',
          fontSize: '12px',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: '500',
            fontSize: '0.8rem',
            color: palette.textPrimary,
          }}
        >
          {label}
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={0.5}
          sx={{
            textAlign: 'left',
            color: palette.textPrimary,
          }}
        >
          <Box>
            <Typography color="text.secondary" fontSize="0.75rem">
              {t('cdp_trips_unit')}
            </Typography>
            <Typography fontWeight="600" variant="subtitle2">
              {totalTrips}
            </Typography>
          </Box>

          <Box>
            <Typography color="text.secondary" fontSize="0.75rem">
              Tons
            </Typography>
            <Typography fontWeight="600" variant="subtitle2">
              {truncateTo2dp(totalTons)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          paddingRight: '16px',
          backgroundColor: enableDarkTheme ? 'background.paper' : '#FFFFFF',
          borderRadius: 2,
          overflow: 'visible',
          '& .apexcharts-canvas': { overflow: 'visible' },
          '& .apexcharts-svg': { overflow: 'visible' },
        }}
      >
        <ReactApexChart
          options={assetChartOptions}
          series={assetSeries}
          type="rangeBar"
          height={60}
          key={Date.now()}
        />
      </Box>
    </Box>
  );
};

export const AssetWiseTimelineChart = ({
  masterAlgoData,
  selectedRange,
  currentShiftStartUtc: _currentShiftStartUtc,
  currentShiftEndUtc: _currentShiftEndUtc,
}: {
  masterAlgoData: DispatchPlanGroupedByBumperNoType;
  selectedRange: string;
  currentShiftStartUtc?: string;
  currentShiftEndUtc?: string;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { customerTz } = useCustomerInfo();

  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const mergedPlans = mergeDispatchPlans(approvedPlanData.plans);

  const { startDateWithTime, endDateWithTime } = useMemo(() => {
    if (selectedRange === 'Today') {
      const { masterRange, startDateWithTime } = getPlanTimeRange(
        masterAlgoData.planData,
        mergedPlans.planData,
        customerTz,
        false
      );
      return {
        startDateWithTime,
        endDateWithTime: masterRange.endDateWithTime,
      };
    } else {
      const temp = getPlanTimeRange(
        masterAlgoData.planData,
        mergedPlans.planData,
        customerTz,
        false
      );
      return temp;
    }
  }, [
    masterAlgoData.planData,
    mergedPlans.planData,
    customerTz,
    selectedRange,
  ]);

  const { groupedPlanData: aggregatedDataByBumperNumber, groupedMetrics } =
    useMemo(
      () => aggregateSchedulePlanByBumperNumber(mergedPlans.planData),
      [mergedPlans.planData]
    );

  const baseChartOptions = useMemo<ApexOptions>(() => {
    return {
      chart: {
        type: 'rangeBar',
        background: 'transparent',
        animations: {
          enabled: false,
          dynamicAnimation: {
            enabled: false,
          },
          animateGradually: {
            enabled: false,
          },
        },
        toolbar: {
          show: false,
          tools: { download: false },
        },
        zoom: {
          enabled: false,
        },
        selection: {
          enabled: false,
        },
        brush: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: { horizontal: true, barHeight: '100%', borderRadius: 2 },
      },
      states: { hover: { filter: { type: 'darken', value: 0.1 } } },
      colors: shiftColorPalette,
      xaxis: {
        type: 'datetime',
        min: startDateWithTime,
        max: endDateWithTime,
        labels: {
          datetimeUTC: false,
          style: {
            colors: palette.textPrimary,
          },
          formatter: (value: any) =>
            convertUtcToDisplayTime(value, 'configured', customerTz, 'HH:mm'),
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: palette.textPrimary,
          },
        },
      },
      grid: {
        yaxis: { lines: { show: false } },
      },
      stroke: { width: 1 },
      fill: { type: 'solid', opacity: 0.7 },
    };
  }, [enableDarkTheme, customerTz, startDateWithTime, endDateWithTime]);

  const masterChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        id: 'masterTimeline',
      },
      yaxis: { show: false },
      grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
    }),
    [baseChartOptions]
  );

  const isBothTimeLineDataEmpty = useMemo(() => {
    const tempMasterAlgoFlatData = [
      { data: Object.values(masterAlgoData.planData).flat() },
    ];
    const tempApprovedPlanData = [
      { data: Object.values(mergedPlans.planData).flat() },
    ];

    const isBothEmpty =
      isAllTimeLineDataEmpty(tempMasterAlgoFlatData) &&
      isAllTimeLineDataEmpty(tempApprovedPlanData);
    return isBothEmpty;
  }, [masterAlgoData.planData, mergedPlans.planData]);

  const assetChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        sparkline: {
          enabled: true,
        },
        parentHeightOffset: 0,
      },
      tooltip: {
        fixed: {
          enabled: true,
          position: 'topLeft',
          offsetX: 160,
          offsetY: 0,
        },
        custom: (props) =>
          AssetWiseTimeLineChartTooltip({
            ...props,
            isMarginTop: false,
            preferredTimeDisplayMode: 'configured',
            timezone: customerTz,
          }),
      },
      xaxis: {
        ...baseChartOptions.xaxis,
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: { show: false },
      grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
      legend: { show: false },
    }),
    [baseChartOptions]
  );

  return (
    <Paper
      sx={{
        borderRadius: 2,
        p: 2,
        border: '1px solid',
        borderColor: `borderColor.grey`,
      }}
      elevation={0}
    >
      {isBothTimeLineDataEmpty ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <WarningAmberRounded />
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '14px',
              ml: 1,
            }}
          >
            {t('no_timeline_data_available')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display={'flex'} alignItems={'center'} gap={2}>
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l0.fill,
                      border: `1px solid ${locationColors.l0.stroke}`,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l0')}
                  </span>
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l1.fill,
                      border: `1px solid ${locationColors.l1.stroke}`,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l1')}
                  </span>
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.other.fill,
                      border: `1px solid ${locationColors.other.stroke}`,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>{t('loading_other')}</span>
                </Box>
              </Box>
              <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: 'rgba(220, 38, 38, 0.7)',
                  }}
                />
                <span style={{ fontSize: '12px' }}>{t('dumping')}</span>
              </Box>
            </Box>
          </Box>

          <Box sx={{ paddingLeft: '159px', paddingRight: '16px' }}>
            <ReactApexChart
              options={masterChartOptions}
              series={[{ data: [] }]}
              type="rangeBar"
              height={50}
              key={Date.now()}
            />
          </Box>

          <Box
            sx={{ mt: '-10px' }}
            maxHeight={'500px'}
            overflow={'auto'}
            display={'flex'}
            flexDirection={'column'}
            gap={2}
          >
            {Object.entries(masterAlgoData.planData).map(
              ([bumperNumber, deviceData]) => {
                const masterAlgoAssetTripsCount =
                  masterAlgoData.trips_count[bumperNumber] || {};
                const masterAlgoAssetMaterialDump =
                  masterAlgoData.material_dump[bumperNumber] || {};

                const masterAlgoAssetSeries = generateSeriesForAsset(
                  deviceData,
                  'Actual Data'
                );

                const approvedPlanAssetData =
                  aggregatedDataByBumperNumber?.[bumperNumber] || [];
                const approvedPlanAssetTripsCount =
                  groupedMetrics?.trip?.[bumperNumber] || {};
                const approvedPlanAssetMaterialDump =
                  groupedMetrics?.material_dumped?.[bumperNumber] || {};

                const approvedPlanAssetSeries = generateSeriesForAsset(
                  approvedPlanAssetData,
                  'Approved Plan'
                );

                return (
                  <Box
                    key={bumperNumber}
                    sx={{
                      border: `1px solid ${
                        enableDarkTheme ? '#334155' : '#E2E8F0'
                      }`,
                      p: 1,
                      backgroundColor: 'customBackground.section',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        color: palette.textPrimary,
                      }}
                    >
                      {bumperNumber}
                    </Typography>
                    <SingleAssetTimelineRow
                      assetChartOptions={assetChartOptions}
                      assetSeries={masterAlgoAssetSeries}
                      totalTrips={masterAlgoAssetTripsCount?.total ?? 0}
                      totalTons={masterAlgoAssetMaterialDump?.total ?? 0}
                      label={t('actual_data')}
                    />

                    <SingleAssetTimelineRow
                      assetChartOptions={assetChartOptions}
                      assetSeries={approvedPlanAssetSeries}
                      totalTrips={approvedPlanAssetTripsCount?.total ?? 0}
                      totalTons={approvedPlanAssetMaterialDump?.total ?? 0}
                      label={t('approved_plan')}
                    />
                  </Box>
                );
              }
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};
